import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { MatchRing } from "@/components/MatchRing";
import { toast } from "sonner";
import { Upload, FileText, X, Sparkles, AlertCircle, CheckCircle2, Mail } from "lucide-react";
import { InviteDialog } from "@/components/InviteDialog";

const MAX_FILES = 15;
const ACCEPT = ".pdf,.docx,.txt";

export const BulkScoreDialog = ({ jobId, jobTitle, open, onOpenChange }) => {
  const [files, setFiles] = useState([]);
  const [scoring, setScoring] = useState(false);
  const [results, setResults] = useState(null);
  const inputRef = useRef(null);

  const reset = () => {
    setFiles([]);
    setResults(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const addFiles = (list) => {
    const arr = Array.from(list || []).filter((f) =>
      /\.(pdf|docx|txt)$/i.test(f.name)
    );
    const combined = [...files, ...arr].slice(0, MAX_FILES);
    setFiles(combined);
  };

  const remove = (idx) => {
    setFiles(files.filter((_, i) => i !== idx));
  };

  const runScore = async () => {
    if (files.length === 0) return toast.error("Add at least one resume");
    setScoring(true);
    setResults(null);
    try {
      const fd = new FormData();
      fd.append("job_id", jobId);
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/match/bulk", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      setResults(data);
      toast.success(
        `Scored ${data.counts?.scored ?? 0} of ${data.counts?.total ?? 0} resumes`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk scoring failed");
    } finally {
      setScoring(false);
    }
  };

  const handleClose = (o) => {
    if (!o && !scoring) {
      reset();
    }
    onOpenChange(o);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="bg-[#111827] border-white/10 text-slate-100 max-w-3xl max-h-[85vh] overflow-y-auto"
        data-testid="bulk-score-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#06B6D4]" />
            Bulk score resumes
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Drop up to {MAX_FILES} resumes (PDF, DOCX, TXT). Each gets scored against
            <span className="text-slate-200"> {jobTitle}</span> in parallel using Gemini 3 Flash.
          </DialogDescription>
        </DialogHeader>

        {!results && (
          <>
            <DropZone
              onFilesSelected={addFiles}
              disabled={scoring || files.length >= MAX_FILES}
              onClickPick={() => inputRef.current?.click()}
            />
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={ACCEPT}
              onChange={(e) => addFiles(e.target.files)}
              className="hidden"
              data-testid="bulk-file-input"
            />

            {files.length > 0 && (
              <div className="space-y-1.5 max-h-64 overflow-y-auto mt-4">
                {files.map((f, i) => (
                  <div
                    key={`${f.name}-${i}`}
                    className="flex items-center gap-3 border border-white/10 bg-[#0B0F19] rounded-lg px-3 py-2"
                  >
                    <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{f.name}</div>
                      <div className="text-[10px] text-slate-500 font-mono uppercase">
                        {(f.size / 1024).toFixed(0)} KB
                      </div>
                    </div>
                    <button
                      onClick={() => remove(i)}
                      disabled={scoring}
                      className="text-slate-500 hover:text-white p-1"
                      data-testid={`bulk-remove-${i}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/10">
              <div className="text-xs text-slate-500 font-mono">
                {files.length} / {MAX_FILES} FILES
              </div>
              <Button
                onClick={runScore}
                disabled={scoring || files.length === 0}
                data-testid="bulk-score-run-btn"
                className="bg-[#06B6D4] hover:bg-[#0891B2] text-[#0B0F19] rounded-full font-medium"
              >
                {scoring ? (
                  <>
                    <span className="w-1.5 h-1.5 bg-[#0B0F19] rounded-full mr-2 pulse-ring" />
                    Scoring…
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Score {files.length} resume{files.length === 1 ? "" : "s"}
                  </>
                )}
              </Button>
            </div>
          </>
        )}

        {results && (
          <ResultsTable results={results.results} jobId={jobId} onReset={reset} />
        )}
      </DialogContent>
    </Dialog>
  );
};

const DropZone = ({ onFilesSelected, disabled, onClickPick }) => {
  const [isDragging, setIsDragging] = useState(false);
  return (
    <button
      type="button"
      onClick={onClickPick}
      disabled={disabled}
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragging(false);
        onFilesSelected(e.dataTransfer.files);
      }}
      data-testid="bulk-dropzone"
      className={`w-full mt-4 border-2 border-dashed rounded-xl px-6 py-10 text-center transition-colors ${
        disabled
          ? "border-white/5 bg-white/[0.02] cursor-not-allowed"
          : isDragging
          ? "border-[#06B6D4] bg-[#06B6D4]/5"
          : "border-white/15 hover:border-white/25 bg-[#0B0F19] hover:bg-white/[0.03]"
      }`}
    >
      <Upload className="w-8 h-8 text-slate-500 mx-auto mb-3" />
      <div className="text-sm text-slate-300">
        <span className="text-[#06B6D4]">Click to browse</span> or drag &amp; drop resumes
      </div>
      <div className="text-xs text-slate-500 mt-1 font-mono">PDF · DOCX · TXT · 5MB each</div>
    </button>
  );
};

const ResultsTable = ({ results, jobId, onReset }) => {
  const scored = results.filter((r) => r.match);
  const errored = results.filter((r) => r.error);
  const [invited, setInvited] = useState({});
  const [inviteTarget, setInviteTarget] = useState(null);

  return (
    <div className="mt-4">
      <InviteDialog
        open={!!inviteTarget}
        onOpenChange={(o) => !o && setInviteTarget(null)}
        jobId={jobId}
        candidate={inviteTarget?.candidate}
        onSent={() => {
          if (inviteTarget) setInvited((s) => ({ ...s, [inviteTarget.idx]: true }));
        }}
      />
      <div className="border border-white/10 rounded-xl overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 border-b border-white/10 mono-label text-slate-500 text-[10px]">
          <div className="col-span-1">#</div>
          <div className="col-span-5">Candidate / file</div>
          <div className="col-span-2 text-center">Skills</div>
          <div className="col-span-1 text-center">Exp</div>
          <div className="col-span-1 text-right">Overall</div>
          <div className="col-span-2 text-right">Action</div>
        </div>
        {scored.map((r, idx) => (
          <div
            key={`${r.filename}-${idx}`}
            data-testid={`bulk-result-${idx}`}
            className={`grid grid-cols-12 items-center px-4 py-3 text-sm ${
              idx !== scored.length - 1 ? "border-b border-white/5" : ""
            }`}
          >
            <div className="col-span-1 text-xs text-slate-500 font-mono">{idx + 1}</div>
            <div className="col-span-5 min-w-0">
              <div className="text-slate-200 truncate">{r.candidate_name}</div>
              <div className="text-[11px] text-slate-500 truncate flex items-center gap-1">
                <span className="truncate">{r.filename}</span>
                {r.candidate_email && (
                  <span className="text-[#06B6D4]/70 truncate">· {r.candidate_email}</span>
                )}
              </div>
            </div>
            <div className="col-span-2 text-center">
              <ScoreCell v={r.match.skills_match} />
            </div>
            <div className="col-span-1 text-center">
              <ScoreCell v={r.match.experience_match} />
            </div>
            <div className="col-span-1 flex justify-end">
              <MatchRing value={r.match.overall} size={40} stroke={4} />
            </div>
            <div className="col-span-2 flex justify-end">
              {r.candidate_email ? (
                <Button
                  size="sm"
                  onClick={() => setInviteTarget({
                    idx,
                    candidate: { email: r.candidate_email, name: r.candidate_name },
                  })}
                  disabled={invited[idx]}
                  data-testid={`bulk-invite-${idx}`}
                  variant="outline"
                  className={`bg-transparent border-white/15 rounded-full h-7 text-xs hover:text-white ${
                    invited[idx]
                      ? "text-[#10B981] border-[#10B981]/40"
                      : "text-slate-100 hover:bg-white/5"
                  }`}
                >
                  {invited[idx] ? (
                    <>
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Invited
                    </>
                  ) : (
                    <>
                      <Mail className="w-3 h-3 mr-1" /> Invite
                    </>
                  )}
                </Button>
              ) : (
                <span
                  className="text-[10px] text-slate-600 font-mono uppercase"
                  title="No email detected in resume text"
                >
                  no email
                </span>
              )}
            </div>
          </div>
        ))}
        {errored.length > 0 && (
          <div className="border-t border-white/10 bg-[#0B0F19]/60">
            {errored.map((r, i) => (
              <div key={i} className="flex items-center gap-2 px-4 py-2 text-xs text-slate-500">
                <AlertCircle className="w-3.5 h-3.5 text-[#F59E0B]" />
                <span className="truncate">{r.filename}</span>
                <span className="text-slate-600">— {r.error}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
        <div className="text-xs text-slate-500 flex items-center gap-2">
          <CheckCircle2 className="w-3.5 h-3.5 text-[#10B981]" />
          {scored.length} scored · {errored.length} errored
        </div>
        <Button
          onClick={onReset}
          variant="outline"
          size="sm"
          data-testid="bulk-reset-btn"
          className="bg-transparent border-white/15 text-slate-100 hover:bg-white/5 hover:text-white rounded-full"
        >
          Score another batch
        </Button>
      </div>
    </div>
  );
};

const ScoreCell = ({ v }) => {
  const color = v >= 80 ? "#10B981" : v >= 60 ? "#F59E0B" : "#EF4444";
  return (
    <span className="inline-flex items-center gap-1.5 text-sm">
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
      <span style={{ color }}>{v}</span>
    </span>
  );
};
